import { dateProvider } from '@utils';
import { Alert, Modal, Button } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Wrapper,
  AlertTitle,
  ContentWrapper,
  DatePlanificationWrapper,
  InfoCardWrapper,
  TextWrapper,
  TitleWrapper
} from './PlanificationResult.styles';

import { Icon } from '@/components/Icon';

const ICON_SIZE = 16;
const MIN_HOURS_TO_SHOW_PLANIFICATION_ALERT = 7;
const MODAL_WIDTH = 572;

interface IPlanificationResultProps {
  resultModalOpen: boolean;
  planificationHours: number;
  planificationMinutes: number;
  planificationEndDate: Date | undefined;
  handleSave: () => void;
  handleBack: () => void;
}

export const PlanificationResult: React.FC<IPlanificationResultProps> = ({
  resultModalOpen,
  planificationHours,
  planificationMinutes,
  planificationEndDate,
  handleSave,
  handleBack
}) => {
  const { t } = useTranslation(['DefaultTranslations', 'StudentProfileConfigurationTranslations']);

  const daysDiff = useMemo(() => {
    if (!planificationEndDate) return 0;
    return dateProvider.diff(new Date(), planificationEndDate, 'days');
  }, [planificationEndDate]);

  return (
    <Modal
      open={resultModalOpen}
      onCancel={handleBack}
      centered
      width={MODAL_WIDTH}
      footer={[
        <Button key="back" onClick={handleBack}>
          {t('DefaultTranslations:HOW_IS_GOING_REPLANIFY')}
        </Button>,
        <Button key="save" type="primary" onClick={handleSave}>
          {t('DefaultTranslations:SAVE_CHANGES')}
        </Button>
      ]}>
      <Wrapper>
        <AlertTitle>{t('DefaultTranslations:RESULT')}</AlertTitle>
        {planificationHours > MIN_HOURS_TO_SHOW_PLANIFICATION_ALERT ? (
          <Alert
            style={{ width: '100%' }}
            showIcon
            icon={<Icon i={'WarningFilled'} color={'orange6'} size={ICON_SIZE} />}
            message={t('DefaultTranslations:PLANIFICATION_RESULT_ALERT_MESSAGE_TITLE_WARNING')}
            description={t(
              'DefaultTranslations:PLANIFICATION_RESULT_ALERT_MESSAGE_CONTENT_WARNING'
            )}
            type="warning"
          />
        ) : (
          <Alert
            style={{ width: '100%' }}
            showIcon
            icon={<Icon i={'CheckmarkFilled'} color={'green6'} size={ICON_SIZE} />}
            message={t('DefaultTranslations:PLANIFICATION_RESULT_ALERT_MESSAGE_TITLE_SUCCESS')}
            description={t(
              'DefaultTranslations:PLANIFICATION_RESULT_ALERT_MESSAGE_CONTENT_SUCCESS'
            )}
            type="success"
          />
        )}
        <DatePlanificationWrapper>
          <InfoCardWrapper>
            <span>
              <Icon i={'Event'} color={'purple6'} size={ICON_SIZE} />
            </span>
            <TextWrapper vertical gap={4}>
              <TitleWrapper>{t('StudentProfileConfigurationTranslations:END_DATE')}</TitleWrapper>
              <ContentWrapper>
                {dateProvider.format(planificationEndDate, 'dd/MM/yyyy')} (
                {t('DefaultTranslations:DAYS_other', { count: daysDiff })})
              </ContentWrapper>
            </TextWrapper>
          </InfoCardWrapper>
          <InfoCardWrapper>
            <span>
              <Icon i={'Time'} color={'purple6'} size={ICON_SIZE} />
            </span>

            <TextWrapper vertical gap={4}>
              <TitleWrapper>
                {t('StudentProfileConfigurationTranslations:PLANIFICATION_RESULT_DAILY_TIME')}
              </TitleWrapper>
              <ContentWrapper>
                {t('DefaultTranslations:PLANIFICATION_RESULT_CORRECT_TIME_END_DATE_STUDY_TIME', {
                  hours: planificationHours,
                  minutes: planificationMinutes
                })}
              </ContentWrapper>
            </TextWrapper>
          </InfoCardWrapper>
        </DatePlanificationWrapper>
      </Wrapper>
    </Modal>
  );
};
